/**
 * Unified Compatibility Engine
 *
 * Integrates all compatibility engines and provides a single interface
 * for comprehensive PC build compatibility checking
 */

import type {
  CompatibilityResult,
  CompatibilityContext
} from '../types/CompatibilityResult'
import type { CPUModel } from '../../models/cpu/cpu-model'
import type { GPUModel } from '../../models/gpu/gpu-model'
import type { MotherboardModel } from '../../models/motherboard/motherboard-model'
import type { MemoryModel } from '../../models/memory/memory-model'
import type { StorageModel } from '../../models/storage/storage-model'
import type { PSUModel } from '../../models/psu/psu-model'
import type { CaseListItem } from '../../models/case/case-list-model'
import type { CaseDetailModel } from '../../models/case/case-detail-model'
import type { CoolerModel } from '../../models/cooler/cpu-cooler-model'

import { PhysicalCompatibilityEngine } from './PhysicalCompatibilityEngine'
import { PerformanceOptimizationEngine } from './PerformanceOptimizationEngine'
import { BIOSCompatibilityEngine } from './BIOSCompatibilityEngine'
import { PowerCompatibilityEngine } from './PowerCompatibilityEngine'
import { StorageCompatibilityEngine } from './StorageCompatibilityEngine'

/**
 * Build configuration with actual component models
 */
export interface DetailedBuildConfiguration {
  cpu?: CPUModel
  gpu?: GPUModel
  motherboard?: MotherboardModel
  memory?: MemoryModel[]
  storage?: StorageModel[]
  psu?: PSUModel
  case?: CaseListItem
  caseDetail?: CaseDetailModel
  cooler?: CoolerModel
}

/**
 * Comprehensive compatibility report
 */
export interface CompatibilityReport {
  // Overall compatibility
  isCompatible: boolean
  overallScore: number
  confidenceLevel: 'high' | 'medium' | 'low'

  // Category scores
  scores: {
    physical: number
    performance: number
    bios: number
    power: number
    storage: number
  }

  // Issues by severity
  issues: {
    critical: CompatibilityResult[]
    warnings: CompatibilityResult[]
    info: CompatibilityResult[]
  }

  // Recommendations
  recommendations: {
    immediate: string[]    // Must fix before building
    suggested: string[]    // Recommended improvements
    optional: string[]     // Nice to have optimizations
  }

  // Performance estimates
  performance?: {
    gaming?: {
      estimatedFPS: Record<string, number> // resolution -> fps
      bottleneckComponents: string[]
    }
    productivity?: {
      relativePerformance: number // 0-100 scale
      strongSuits: string[]
      limitations: string[]
    }
  }

  // Detailed results by engine
  detailed: {
    physical: CompatibilityResult[]
    performance: CompatibilityResult[]
    bios: CompatibilityResult[]
    power: CompatibilityResult[]
    storage: CompatibilityResult[]
  }

  // Build viability assessment
  viability: 'excellent' | 'good' | 'acceptable' | 'problematic' | 'incompatible'

  // Generation timestamp
  timestamp: number
}

/**
 * Unified Compatibility Engine
 * Orchestrates all compatibility engines
 */
export class UnifiedCompatibilityEngine {
  private physicalEngine = new PhysicalCompatibilityEngine()
  private performanceEngine = new PerformanceOptimizationEngine()
  private biosEngine = new BIOSCompatibilityEngine()
  private powerEngine = new PowerCompatibilityEngine()
  private storageEngine = new StorageCompatibilityEngine()

  /**
   * Perform comprehensive compatibility analysis
   */
  async analyzeCompatibility(
    config: DetailedBuildConfiguration,
    context?: CompatibilityContext
  ): Promise<CompatibilityReport> {
    const startTime = globalThis.performance.now()

    // Run all compatibility checks in parallel for performance
    const [
      physicalResults,
      performanceResults,
      biosResults,
      powerResults,
      storageResults
    ] = await Promise.all([
      this.checkPhysicalCompatibility(config, context),
      this.checkPerformanceCompatibility(config, context),
      this.checkBIOSCompatibility(config, context),
      this.checkPowerCompatibility(config, context),
      this.checkStorageCompatibility(config, context)
    ])

    // Calculate category scores
    const scores = {
      physical: this.calculateCategoryScore(physicalResults),
      performance: this.calculateCategoryScore(performanceResults),
      bios: this.calculateCategoryScore(biosResults),
      power: this.calculateCategoryScore(powerResults),
      storage: this.calculateCategoryScore(storageResults)
    }

    // Combine all results
    const allResults = [
      ...physicalResults,
      ...performanceResults,
      ...biosResults,
      ...powerResults,
      ...storageResults
    ]

    // Categorize issues by severity
    const issues = this.categorizeIssues(allResults)

    // Calculate overall compatibility
    const overallScore = this.calculateOverallScore(scores)
    const isCompatible = this.assessCompatibility(scores, issues)
    const viability = this.assessViability(scores, issues)
    const confidenceLevel = this.assessConfidenceLevel(config, allResults)

    // Generate recommendations
    const recommendations = this.generateRecommendations(allResults, scores)

    // Generate performance estimates
    const perfEstimates = this.generatePerformanceEstimates(config, performanceResults)

    const processingTime = globalThis.performance.now() - startTime
    if (processingTime > 100) {
      console.warn(`Compatibility analysis took ${processingTime.toFixed(2)}ms`)
    }

    return {
      isCompatible,
      overallScore,
      confidenceLevel,
      scores,
      issues,
      recommendations,
      ...(perfEstimates != null ? { performance: perfEstimates } : {}),
      detailed: {
        physical: physicalResults,
        performance: performanceResults,
        bios: biosResults,
        power: powerResults,
        storage: storageResults
      },
      viability,
      timestamp: Date.now()
    }
  }

  /**
   * Quick compatibility check for essential compatibility only
   */
  async quickCompatibilityCheck(
    config: DetailedBuildConfiguration
  ): Promise<{ isCompatible: boolean; criticalIssues: CompatibilityResult[] }> {
    const criticalChecks = await Promise.all([
      this.checkCPUMotherboardSocket(config),
      this.checkMemoryCompatibility(config),
      this.checkPowerAdequacy(config)
    ])

    const criticalIssues = criticalChecks.flat().filter(result => result.level === 'critical')

    return {
      isCompatible: criticalIssues.length === 0,
      criticalIssues
    }
  }

  private checkPhysicalCompatibility(
    config: DetailedBuildConfiguration,
    _context?: CompatibilityContext
  ): CompatibilityResult[] {
    const results: CompatibilityResult[] = []

    if (config.memory && config.cooler) {
      results.push(...this.physicalEngine.checkMemoryCoolerCompatibility(config.memory[0], config.cooler))
    }

    if (config.gpu && config.case) {
      results.push(...this.physicalEngine.checkGPUCaseCompatibility(config.gpu, config.case))
    }

    // Phase 1: Cooler-Case height compatibility
    if (config.cooler && config.case) {
      results.push(...this.physicalEngine.checkCoolerCaseCompatibility(config.cooler, config.case))
    }

    // Phase 1: PSU-Case form factor compatibility
    if (config.psu && config.case) {
      // Convert PSUModel to PSUListItem-like object for compatibility check
      const psuListItem = {
        id: config.psu.id,
        model: config.psu.model,
        manufacturer: config.psu.manufacturer,
        status: config.psu.status,
        wattage: config.psu.wattage,
        efficiency: config.psu.efficiency,
        modular: config.psu.modular,
        formFactor: config.psu.formFactor
      }
      results.push(...this.physicalEngine.checkPSUCaseCompatibility(psuListItem, config.case))
    }

    // Phase 3: AIO-Case radiator compatibility
    if (config.cooler && config.caseDetail) {
      results.push(...this.physicalEngine.checkAIOCaseCompatibility(config.cooler, config.caseDetail))

      // Phase 4: AIO detailed compatibility (mount location constraints)
      const detailedChecks = this.physicalEngine.checkAIODetailedCompatibility(
        config.cooler,
        config.caseDetail,
        {
          gpu: config.gpu,
          memory: config.memory?.[0]
        }
      )
      results.push(...detailedChecks)
    }

    return results
  }

  private checkPerformanceCompatibility(
    config: DetailedBuildConfiguration,
    _context?: CompatibilityContext
  ): CompatibilityResult[] {
    const results: CompatibilityResult[] = []

    if (config.cpu && config.memory) {
      results.push(...this.performanceEngine.checkCPUMemoryOptimization(config.cpu, config.memory[0]))
    }

    if (config.cpu && config.motherboard) {
      results.push(...this.performanceEngine.checkVRMAdequacy(config.cpu, config.motherboard))
    }

    return results
  }

  private checkBIOSCompatibility(
    config: DetailedBuildConfiguration,
    context?: CompatibilityContext
  ): CompatibilityResult[] {
    if (!config.cpu || !config.motherboard) return []

    return this.biosEngine.checkBIOSCompatibility(config.cpu, config.motherboard, context)
  }

  private checkPowerCompatibility(
    config: DetailedBuildConfiguration,
    context?: CompatibilityContext
  ): CompatibilityResult[] {
    if (!config.psu) return []

    const systemComponents = {
      ...(config.cpu != null ? { cpu: config.cpu } : {}),
      ...(config.gpu != null ? { gpu: config.gpu } : {}),
      ...(config.motherboard != null ? { motherboard: config.motherboard } : {}),
      ...(config.memory != null ? { memory: config.memory } : {}),
      ...(config.storage != null ? { storage: config.storage } : {})
    }

    return this.powerEngine.checkPowerCompatibility(config.psu, systemComponents, context)
  }

  private checkStorageCompatibility(
    config: DetailedBuildConfiguration,
    _context?: CompatibilityContext
  ): CompatibilityResult[] {
    if (!config.storage || !config.motherboard) return []

    const results: CompatibilityResult[] = []

    for (const storage of config.storage) {
      results.push(...this.storageEngine.checkStorageCompatibility(storage, config.motherboard, _context))
    }

    return results
  }

  private checkCPUMotherboardSocket(config: DetailedBuildConfiguration): CompatibilityResult[] {
    if (!config.cpu || !config.motherboard) return []

    const cpuSocket = config.cpu.socket
    const mbSocket = config.motherboard.cpuSocket?.type

    if (cpuSocket !== mbSocket) {
      return [{
        level: 'critical',
        category: 'socket',
        message: 'CPUとマザーボードのソケットが一致しません',
        details: `CPU: ${cpuSocket}, マザーボード: ${mbSocket ?? 'Unknown'}`,
        solutions: [
          `${cpuSocket}対応マザーボードを選択`,
          `${mbSocket}対応CPUを選択`
        ],
        score: 0
      }]
    }

    return []
  }

  private checkMemoryCompatibility(config: DetailedBuildConfiguration): CompatibilityResult[] {
    if (!config.cpu || !config.memory) return []

    const cpuMemoryType = config.cpu.memorySupport?.type
    const memoryType = config.memory[0]?.type

    if (cpuMemoryType && memoryType && !cpuMemoryType.includes(memoryType)) {
      return [{
        level: 'critical',
        category: 'memory',
        message: 'CPUとメモリのDDR規格が一致しません',
        details: `CPU: ${cpuMemoryType}, メモリ: ${memoryType}`,
        solutions: [
          `${cpuMemoryType}対応メモリを選択`,
          `${memoryType}対応CPUを選択`
        ],
        score: 0
      }]
    }

    return []
  }

  private checkPowerAdequacy(config: DetailedBuildConfiguration): CompatibilityResult[] {
    if (!config.psu) return []

    const powerReq = this.powerEngine.calculateSystemPowerRequirement({
      ...(config.cpu != null ? { cpu: config.cpu } : {}),
      ...(config.gpu != null ? { gpu: config.gpu } : {}),
      ...(config.motherboard != null ? { motherboard: config.motherboard } : {}),
      ...(config.memory != null ? { memory: config.memory } : {}),
      ...(config.storage != null ? { storage: config.storage } : {})
    })

    const psuWattage = config.psu.wattage ?? 0

    if (powerReq.totalWattage > psuWattage) {
      return [{
        level: 'critical',
        category: 'power',
        message: 'PSU電力容量が不足しています',
        details: `必要: ${powerReq.totalWattage}W, 利用可能: ${psuWattage}W`,
        solutions: [
          `${powerReq.recommendedPSUWattage}W以上のPSUを選択`,
          'より省電力なコンポーネントを検討'
        ],
        score: 0
      }]
    }

    return []
  }

  private calculateCategoryScore(results: CompatibilityResult[]): number {
    if (results.length === 0) return 100

    const totalScore = results.reduce((sum, result) => sum + (result.score ?? 100), 0)
    return Math.round(totalScore / results.length)
  }

  private calculateOverallScore(scores: CompatibilityReport['scores']): number {
    const weights = {
      physical: 0.25,
      performance: 0.20,
      bios: 0.25,
      power: 0.20,
      storage: 0.10
    }

    return Math.round(
      scores.physical * weights.physical +
      scores.performance * weights.performance +
      scores.bios * weights.bios +
      scores.power * weights.power +
      scores.storage * weights.storage
    )
  }

  private categorizeIssues(results: CompatibilityResult[]) {
    return {
      critical: results.filter(r => r.level === 'critical'),
      warnings: results.filter(r => r.level === 'warning'),
      info: results.filter(r => r.level === 'info')
    }
  }

  private assessCompatibility(scores: CompatibilityReport['scores'], issues: CompatibilityReport['issues']): boolean {
    // Not compatible if there are critical issues
    if (issues.critical.length > 0) return false

    // Not compatible if any core category scores too low
    if (scores.physical < 60 || scores.bios < 60 || scores.power < 60) return false

    return true
  }

  private assessViability(scores: CompatibilityReport['scores'], issues: CompatibilityReport['issues']): CompatibilityReport['viability'] {
    if (issues.critical.length > 0) return 'incompatible'

    const avgScore = Object.values(scores).reduce((sum, score) => sum + score, 0) / Object.values(scores).length

    if (avgScore >= 90 && issues.warnings.length <= 1) return 'excellent'
    if (avgScore >= 80 && issues.warnings.length <= 3) return 'good'
    if (avgScore >= 65 && issues.warnings.length <= 5) return 'acceptable'

    return 'problematic'
  }

  private assessConfidenceLevel(config: DetailedBuildConfiguration, results: CompatibilityResult[]): 'high' | 'medium' | 'low' {
    const componentCount = Object.values(config).filter(Boolean).length
    const resultsWithDetails = results.filter(r => r.details).length

    if (componentCount >= 6 && resultsWithDetails >= results.length * 0.8) return 'high'
    if (componentCount >= 4 && resultsWithDetails >= results.length * 0.6) return 'medium'

    return 'low'
  }

  private generateRecommendations(results: CompatibilityResult[], scores: CompatibilityReport['scores']) {
    const immediate: string[] = []
    const suggested: string[] = []
    const optional: string[] = []

    results.forEach(result => {
      if (result.solutions) {
        if (result.level === 'critical') {
          immediate.push(...result.solutions)
        } else if (result.level === 'warning') {
          suggested.push(...result.solutions)
        } else {
          optional.push(...result.solutions)
        }
      }
    })

    // Add performance optimization suggestions
    if (scores.performance < 80) {
      suggested.push('パフォーマンス最適化のためのコンポーネント見直しを検討')
    }

    return {
      immediate: [...new Set(immediate)],
      suggested: [...new Set(suggested)],
      optional: [...new Set(optional)]
    }
  }

  private generatePerformanceEstimates(config: DetailedBuildConfiguration, performanceResults: CompatibilityResult[]): CompatibilityReport['performance'] {
    // Simplified performance estimation - would be more complex in real implementation
    if (!config.cpu || !config.gpu) return undefined

    const cpuTier = this.estimateCPUTier(config.cpu)
    const gpuTier = this.estimateGPUTier(config.gpu)

    return {
      gaming: {
        estimatedFPS: {
          '1080p': Math.min(cpuTier * 30, gpuTier * 25),
          '1440p': Math.min(cpuTier * 20, gpuTier * 20),
          '4K': Math.min(cpuTier * 10, gpuTier * 15)
        },
        bottleneckComponents: performanceResults
          .filter(r => r.message.includes('ボトルネック'))
          .map(r => r.message)
      },
      productivity: {
        relativePerformance: Math.min(100, (cpuTier + gpuTier) / 2 * 10),
        strongSuits: cpuTier > 8 ? ['マルチタスク', 'レンダリング'] : ['基本作業'],
        limitations: cpuTier < 5 ? ['重い処理', '大量データ'] : []
      }
    }
  }

  private estimateCPUTier(cpu: CPUModel): number {
    const cores = cpu.coreCount ?? 4
    const frequency = cpu.baseClock ?? 3.0
    const tdp = cpu.tdp || 65

    // Simplified scoring based on specs
    return Math.min(10, Math.round((cores * (frequency * 1000) * tdp) / 100000))
  }

  private estimateGPUTier(gpu: GPUModel): number {
    const memory = gpu.memorySize ?? 4
    const tdp = gpu.tdp || 150

    // Simplified scoring based on specs
    return Math.min(10, Math.round((memory * tdp) / 100))
  }
}

// Export the unified engine as default
export default UnifiedCompatibilityEngine

// Export individual engines for direct access if needed
export {
  PhysicalCompatibilityEngine,
  PerformanceOptimizationEngine,
  BIOSCompatibilityEngine,
  PowerCompatibilityEngine,
  StorageCompatibilityEngine
}